/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// 6-digit OTP entry matching Figma file gtBsWqJJcI6ZBuBv51RU35 node
// 3281:98470 (giant-buttons-group).
//
//   slot       : 48 × 48, radius 8, bg `surface-panel`, border `border-subtle`
//   gap        : 8 between slots, "-" separator after slot 3
//   filled     : Lato 700 / 20 / 28, `text-primary`
//   empty      : same font, `text-muted` (placeholder "0")
//   focused    : 1.5 px ring `accent-brand` replacing default border

import { forwardRef, useEffect, useRef, useState } from 'react';
import { Pressable, Text, TextInput, View, useColorScheme } from 'react-native';
import { tokens } from '../design-system/tokens';

const SLOT_COUNT = 6;

export interface AuthOtpInputProps {
	readonly value: string;
	readonly onChange: (next: string) => void;
	readonly onComplete?: (full: string) => void;
	readonly autoFocus?: boolean;
	readonly hasError?: boolean;
	readonly disabled?: boolean;
	readonly accessibilityLabel?: string;
}

export function AuthOtpInput(props: AuthOtpInputProps): JSX.Element {
	const { value, onChange, onComplete, autoFocus, hasError, disabled, accessibilityLabel } = props;
	const inputs = useRef<Array<TextInput | null>>([]);
	const [focusedIndex, setFocusedIndex] = useState<number>(-1);
	const digits = normalize(value);

	useEffect(() => {
		if (digits.length === SLOT_COUNT && onComplete) {
			onComplete(digits);
		}
	}, [digits, onComplete]);

	const focus = (index: number): void => {
		const clamped = Math.max(0, Math.min(SLOT_COUNT - 1, index));
		inputs.current[clamped]?.focus();
	};

	const handleChange = (index: number, next: string): void => {
		const onlyDigits = next.replace(/\D+/g, '');
		if (onlyDigits.length === 0) {
			const cleared = digits.slice(0, index) + ' ' + digits.slice(index + 1);
			onChange(cleared.replace(/\s/g, ''));
			return;
		}
		const combined = (
			digits.slice(0, index) + onlyDigits + digits.slice(index + onlyDigits.length)
		)
			.replace(/\s/g, '')
			.slice(0, SLOT_COUNT);
		onChange(combined);
		const writtenLen = Math.min(onlyDigits.length, SLOT_COUNT - index);
		focus(index + writtenLen);
	};

	const handleKeyPress = (index: number, key: string): void => {
		if (key !== 'Backspace') return;
		if (digits[index]) return;
		const previous = Math.max(0, index - 1);
		const cleared = digits.slice(0, previous);
		onChange(cleared);
		focus(previous);
	};

	return (
		<View
			accessibilityLabel={accessibilityLabel ?? 'ping_auth_otp'}
			className="flex-row items-center justify-center gap-sm"
		>
			{Array.from({ length: SLOT_COUNT }).map((_, index) => (
				<View key={index} className="flex-row items-center gap-sm">
					{index === SLOT_COUNT / 2 ? (
						<Text className="font-sans font-bold text-[20px] leading-[28px] text-light-text-muted dark:text-dark-text-muted">
							-
						</Text>
					) : null}
					<Slot
						ref={(node) => {
							inputs.current[index] = node;
						}}
						value={digits[index] ?? ''}
						focused={focusedIndex === index}
						onChangeText={(next) => handleChange(index, next)}
						onKeyPress={(key) => handleKeyPress(index, key)}
						onFocus={() => setFocusedIndex(index)}
						onBlur={() => setFocusedIndex((curr) => (curr === index ? -1 : curr))}
						autoFocus={Boolean(autoFocus) && index === 0}
						disabled={disabled}
						hasError={hasError}
					/>
				</View>
			))}
		</View>
	);
}

function normalize(input: string): string {
	return input.replace(/\D+/g, '').slice(0, SLOT_COUNT);
}

interface SlotProps {
	readonly value: string;
	readonly focused: boolean;
	readonly onChangeText: (next: string) => void;
	readonly onKeyPress: (key: string) => void;
	readonly onFocus: () => void;
	readonly onBlur: () => void;
	readonly autoFocus?: boolean | undefined;
	readonly disabled?: boolean | undefined;
	readonly hasError?: boolean | undefined;
}

const Slot = forwardRef<TextInput, SlotProps>(function Slot(props, ref) {
	const { value, focused, onChangeText, onKeyPress, onFocus, onBlur, autoFocus, disabled, hasError } =
		props;
	const scheme = useColorScheme();
	const c = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'];
	const filled = value.length > 0;
	const placeholderColor = c.text.muted;

	const borderClass = hasError
		? 'border-light-accent-rose dark:border-dark-accent-rose'
		: focused
			? 'border-light-accent-brand dark:border-dark-accent-brand'
			: 'border-light-border-subtle dark:border-dark-border-subtle';
	const textClass = filled
		? 'text-light-text-primary dark:text-dark-text-primary'
		: 'text-light-text-muted dark:text-dark-text-muted';

	// Same vertical-centring pattern as AuthInput in AuthLayout:
	// macOS's NSTextField top-anchors text inside a fixed-height field,
	// so any `h-[48px]` / `leading-[28px]` directly on the TextInput
	// pushes the glyph against the top edge. The Pressable already
	// owns the 48×48 slot — promote it to the visual container (border,
	// bg, flex-center) and let the TextInput auto-size to its glyph
	// height inside, with `padding: 0` to kill NSTextField's default
	// top inset. RN-Web flattens this back to one click-target div
	// containing an auto-sized <input>; vertical centring still works
	// because `<input>` centres single-line content by default.
	return (
		<Pressable
			onPress={onFocus}
			disabled={disabled}
			className={`w-[48px] h-[48px] rounded-[8px] border ${focused ? 'border-[1.5px]' : 'border'} ${borderClass} bg-light-surface-panel dark:bg-dark-surface-panel items-center justify-center`}
		>
			<TextInput
				ref={ref}
				value={value}
				onChangeText={onChangeText}
				onKeyPress={(event) => onKeyPress(event.nativeEvent.key)}
				onFocus={onFocus}
				onBlur={onBlur}
				autoFocus={autoFocus}
				editable={!disabled}
				keyboardType="number-pad"
				inputMode="numeric"
				maxLength={1}
				textContentType="oneTimeCode"
				selectTextOnFocus
				autoComplete="one-time-code"
				placeholder="0"
				placeholderTextColor={placeholderColor}
				{...({ enableFocusRing: false } as { enableFocusRing: boolean })}
				className={`font-sans font-bold text-[20px] outline-none ${textClass}`}
				style={{ padding: 0, textAlign: 'center', minWidth: 18 }}
			/>
		</Pressable>
	);
});
